import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { from, Observable, of, throwError } from "rxjs";
import { catchError, delay, shareReplay, switchMap, tap } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class NetCashService {
  constructor(private http: HttpClientService) {}

  private lotsFilesCache = new Map<string, Observable<any>>();
  private parseCache = new Map<string, Observable<any>>();
  private movementDetailCache = new Map<string, Observable<any>>();

  public parseLotsFiles({
    content,
    date,
    content_hash,
  }: {
    content: string;
    date: string;
    content_hash: string;
  }): Observable<any> {
    return from(of(content_hash)).pipe(
      switchMap((hash) => {
        if (this.lotsFilesCache.has(hash)) {
          return this.lotsFilesCache
            .get(hash)!
            .pipe(tap((data) => console.log("NetCashService.parseLotsFiles: Ok (from cache).")));
        }

        const params = {
          domiciliation_date: date,
          content,
          investor: "NEXU", // Default investor to maintain compatibility with old code
        };

        const request$ = this.http.post("/api/net_cash/parse_domiciliation", params).pipe(
          tap(() => console.log("NetCashService.parseLotsFiles: Ok (cached by hash).")),
          shareReplay(1),
          catchError((err) => {
            this.lotsFilesCache.delete(hash);
            return throwError(() => err);
          })
        );

        this.lotsFilesCache.set(hash, request$);

        return request$;
      })
    );
  }

  public parseReportFile({ content, content_hash }: { content: string; content_hash: string }): Observable<any> {
    return from(of(content_hash)).pipe(
      switchMap((hash) => {
        console.log(`NetCashService.parseReportFile: Hash calculado: ${hash}`);
        if (this.parseCache.has(hash)) {
          return this.parseCache
            .get(hash)!
            .pipe(tap((data) => console.log("NetCashService.parseReportFile: Ok (from cache).")));
        }

        const params = {
          content,
          investor: "NEXU", // Default investor to maintain compatibility with old code
          hash,
        };

        const request$ = this.http.post("/api/net_cash/parse_collection", params).pipe(
          tap((data) => console.log("NetCashService.parseReportFile: Ok (cached by hash).")),
          shareReplay(1),
          catchError((err) => {
            this.parseCache.delete(hash);
            return throwError(() => err);
          })
        );

        this.parseCache.set(hash, request$);

        return request$;
      })
    );
  }

  public parseMovementDetailFile({
    content,
    content_hash,
  }: {
    content: string;
    content_hash: string;
  }): Observable<any> {
    return from(of(content_hash)).pipe(
      switchMap((hash) => {
        if (this.movementDetailCache.has(hash)) {
          return this.movementDetailCache
            .get(hash)!
            .pipe(tap((data) => console.log("NetCashService.parseMovementDetailFile: Ok (from cache).")));
        }

        const params = { content };
        const request$ = this.http.post("/api/net_cash/parse_movements", params).pipe(
          tap((data) => console.log("NetCashService.parseMovementDetailFile: Ok (cached by hash).")),
          shareReplay(1),
          catchError((err) => {
            this.movementDetailCache.delete(hash);
            return throwError(() => err);
          })
        );

        this.movementDetailCache.set(hash, request$);

        return request$;
      })
    );
  }

  public domiciliationDetail(lots_rows, report_rows): Observable<any> {
    const params = {
      domiciliation_rows: lots_rows,
      collection_rows: report_rows,
    };
    return this.http
      .post("/api/net_cash/domiciliations_detail", params)
      .pipe(tap((_) => console.log("NetCashService.domiciliationDetail: Ok.")));
  }

  public movementDetail(detail, movements): Observable<any> {
    const params = {
      domiciliations_detail: detail,
      movements: movements,
    };
    return this.http
      .post("/api/net_cash/movements_detail", params)
      .pipe(tap((_) => console.log("NetCashService.movementDetail: Ok.")));
  }

  public effectivePayment(movements_detail): Observable<any> {
    const params = {
      movements_detail: movements_detail,
    };
    return this.http
      .post("/api/net_cash/effective_payments", params)
      .pipe(tap((_) => console.log("NetCashService.effectivePayment: Ok.")));
  }

  public directEffectivePayment(domiciliation_rows, collection_rows): Observable<any> {
    const params = { domiciliation_rows, collection_rows };
    return this.http
      .post("/api/net_cash/direct_effective_payments", params)
      .pipe(tap((_) => console.log("NetCashService.directEffectivePayment: Ok.")));
  }

  public noOperatedPayments(domiciliation_rows, collection_rows, attempt_number): Observable<any> {
    const params = { domiciliation_rows, collection_rows, attempt_number };
    return this.http
      .post("/api/net_cash/no_operated", params)
      .pipe(tap((_) => console.log("NetCashService.noOperatedPayments: Ok.")));
  }

  public saveOperatedPayments(payments, domi_date, attempt, user_id): Observable<any> {
    const params = { payments, domi_date, attempt, user_id };
    return this.http
      .post("/api/net_cash/domi_payments", params)
      .pipe(tap((_) => console.log("NetCashService.saveOperatedPayments: Ok.")));
  }

  public asPayment(payments, usuario_id): Observable<any> {
    const params = {
      payments: payments,
      usuario_id: usuario_id,
    };
    return this.http
      .post("/api/net_cash/as_payments", params)
      .pipe(tap((_) => console.log("NetCashService.asPayment: Ok.")));
  }
}
